import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ACCENT_COLORS, APP_VERSION, APP_DEVELOPERS, APP_SUPPORTERS, GITHUB_REPO_URL,
         BUILD_VER, BUILD_DATE, API_BASE_URL } from '../utils/constants';
import { loadAllNotes, clearAllNotes, getNotesCount } from '../utils/notesStorage';
import { getAllKeys, removeItem } from '../utils/storage';
import apiService from '../utils/api';

/* ---- SectionHeader ---- */
const SectionHeader = ({ title }) => (
  <div style={{ color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: 0.5, marginTop: 24, marginBottom: 8, paddingLeft: 4 }}>{title}</div>
);

/* ---- SettingsGroup ---- */
const SettingsGroup = ({ children }) => (
  <div style={{ borderRadius: 14, overflow: 'hidden', border: '0.5px solid var(--border)',
    boxShadow: '0 2px 6px var(--shadow-color)' }}>{children}</div>
);

/* ---- SettingsRow ---- */
const SettingsRow = ({ icon, title, subtitle, onClick, isFirst, isLast, rightElement, destructive, accentColor }) => (
  <div onClick={onClick} style={{ display: 'flex', alignItems: 'center', padding: 14, cursor: onClick ? 'pointer' : 'default',
    background: 'var(--surface-secondary)',
    borderTopLeftRadius: isFirst ? 14 : 0, borderTopRightRadius: isFirst ? 14 : 0,
    borderBottomLeftRadius: isLast ? 14 : 0, borderBottomRightRadius: isLast ? 14 : 0,
    borderBottom: isLast ? 'none' : '0.5px solid var(--border)',
    transition: 'opacity 0.15s' }}
    onMouseDown={e => e.currentTarget.style.opacity = '0.7'}
    onMouseUp={e => e.currentTarget.style.opacity = '1'}
    onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--accent-glass)', border: '0.5px solid var(--accent-glass-border)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 12, flexShrink: 0 }}>
      <ion-icon name={icon} style={{ fontSize: 18, color: destructive ? '#ef4444' : 'var(--accent)' }}></ion-icon>
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ color: destructive ? '#ef4444' : 'var(--text)', fontSize: 15, fontWeight: 500 }}>{title}</div>
      {subtitle && <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2, lineHeight: 1.4 }}>{subtitle}</div>}
    </div>
    {rightElement || <ion-icon name="chevron-forward" style={{ fontSize: 18, color: 'var(--text-tertiary)', flexShrink: 0 }}></ion-icon>}
  </div>
);

/* ---- Toggle ---- */
const Toggle = ({ active, onChange }) => (
  <div onClick={(e) => { e.stopPropagation(); onChange(!active); }}
    className={`toggle${active ? ' toggle--active' : ''}`}>
    <div className="toggle__knob" />
  </div>
);

/* ==== Sub-screens ==== */

/* -- AppearanceSettings -- */
const AppearanceSettings = ({ theme, accentColor, setTheme, setAccentColor, developerMode, isNewYearMode, onNewYearModeChange }) => {
  const themes = [
    { id: 'light', label: 'Светлая', icon: 'sunny-outline' },
    { id: 'dark', label: 'Тёмная', icon: 'moon-outline' },
    ...(developerMode ? [{ id: 'matrix', label: 'Матрица', icon: 'terminal-outline' }] : []),
  ];
  const accents = [
    { id: 'green', color: '#10B981', label: 'Зелёный' },
    { id: 'blue', color: '#3B82F6', label: 'Синий' },
    { id: 'purple', color: '#8B5CF6', label: 'Фиолетовый' },
    ...(developerMode ? [
      { id: 'orange', color: '#F97316', label: 'Оранжевый' },
      { id: 'matrix', color: '#00FF41', label: 'Матрица' },
    ] : []),
  ];
  const handleSetTheme = (t) => { setTheme(t); localStorage.setItem('theme', t); };
  const handleSetAccent = (a) => {
    setAccentColor(a);
    localStorage.setItem('accentColor', a);
    if (a === 'matrix') handleSetTheme('matrix');
  };

  return (
    <div style={{ padding: 16 }}>
      <SectionHeader title="Тема" />
      <SettingsGroup>
        {themes.map((t, i) => (
          <SettingsRow key={t.id} icon={t.icon} title={t.label} isFirst={i === 0} isLast={i === themes.length - 1}
            onClick={() => handleSetTheme(t.id)}
            rightElement={theme === t.id ? <ion-icon name="checkmark-circle" style={{ fontSize: 22, color: 'var(--accent)' }}></ion-icon> : null} />
        ))}
      </SettingsGroup>

      <SectionHeader title="Акцентный цвет" />
      <div style={{ display: 'flex', gap: 14, padding: '8px 0', flexWrap: 'wrap' }}>
        {accents.map(a => (
          <div key={a.id} onClick={() => handleSetAccent(a.id)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <div style={{ width: 40, height: 40, borderRadius: 20, background: a.color, border: accentColor === a.id ? '3px solid var(--text)' : '3px solid transparent',
              transform: accentColor === a.id ? 'scale(1.1)' : 'scale(1)', transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {accentColor === a.id && <ion-icon name="checkmark" style={{ fontSize: 18, color: '#fff' }}></ion-icon>}
            </div>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 500 }}>{a.label}</span>
          </div>
        ))}
      </div>

      <SectionHeader title="Новогодний режим" />
      <SettingsGroup>
        <SettingsRow icon="snow-outline" title="Новогодний режим" subtitle="Снегопад и праздничные элементы" isFirst isLast
          onClick={() => {
            const newVal = !isNewYearMode;
            onNewYearModeChange(newVal);
            localStorage.setItem('new_year_mode', JSON.stringify(newVal));
          }}
          rightElement={<Toggle active={isNewYearMode} onChange={(v) => { onNewYearModeChange(v); localStorage.setItem('new_year_mode', JSON.stringify(v)); }} />} />
      </SettingsGroup>
    </div>
  );
};

/* -- AboutScreen -- */
const AboutScreen = ({ theme, accentColor }) => {
  const features = [
    { icon: 'calendar-outline', title: 'Расписание занятий', desc: 'Просмотр расписания по дням и неделям' },
    { icon: 'school-outline', title: 'Первокурснику', desc: 'Полезная информация для новых студентов' },
    { icon: 'newspaper-outline', title: 'Новости', desc: 'Актуальные новости ХГУ' },
    { icon: 'create-outline', title: 'Заметки к парам', desc: 'Записывайте важное прямо в расписании' },
    { icon: 'color-palette-outline', title: 'Темы оформления', desc: 'Светлая, тёмная и секретная тема Матрица' },
  ];
  return (
    <div style={{ padding: 16 }}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ width: 80, height: 80, borderRadius: 22, background: 'var(--accent-glass)', border: '1px solid var(--accent-glass-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
          <ion-icon name="school" style={{ fontSize: 40, color: 'var(--accent)' }}></ion-icon>
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Мой ИТИ ХГУ</div>
        <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 4 }}>Версия {APP_VERSION}</div>
      </div>
      <SectionHeader title="Возможности" />
      <SettingsGroup>
        {features.map((f, i) => (
          <SettingsRow key={i} icon={f.icon} title={f.title} subtitle={f.desc} isFirst={i === 0} isLast={i === features.length - 1} rightElement={<span />} />
        ))}
      </SettingsGroup>
      <SectionHeader title="Разработка" />
      <div style={{ background: 'var(--surface-secondary)', borderRadius: 14, padding: 16, border: '0.5px solid var(--border)' }}>
        <div style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.6 }}>
          Разработано с ❤️ {APP_DEVELOPERS}
        </div>
        <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.6 }}>
          При поддержке {APP_SUPPORTERS}
        </div>
      </div>
    </div>
  );
};

/* -- DeveloperMenu -- */
const DeveloperMenu = ({ theme, accentColor, onResetDeveloperMode }) => {
  const [customApiUrl, setCustomApiUrl] = useState('');
  const [useCustomApi, setUseCustomApi] = useState(false);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [storageKeys, setStorageKeys] = useState([]);
  const [storageValues, setStorageValues] = useState([]);
  const [cacheStats, setCacheStats] = useState(null);
  const [notesStats, setNotesStats] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem('custom_api_url');
    const savedUse = localStorage.getItem('use_custom_api');
    if (saved) setCustomApiUrl(saved);
    if (savedUse === 'true') setUseCustomApi(true);
  }, []);

  const saveApiSettings = () => {
    if (useCustomApi && customApiUrl.trim()) {
      try { new URL(customApiUrl.trim()); } catch {
        alert('Некорректный URL. Убедитесь, что адрес начинается с http:// или https://');
        return;
      }
      localStorage.setItem('custom_api_url', customApiUrl.trim());
      localStorage.setItem('use_custom_api', 'true');
      alert('Кастомный API-эндпоинт сохранён. Перезагрузите страницу для применения.');
    } else {
      localStorage.setItem('use_custom_api', 'false');
      alert('Используется стандартный API-эндпоинт.');
    }
  };

  const loadDebugInfo = () => {
    const keys = getAllKeys();
    setStorageKeys(keys);
    const vals = [];
    const knownKeys = ['theme', 'accentColor', 'selected_group', 'selected_course', 'developer_mode',
      'custom_api_url', 'use_custom_api', 'new_year_mode', 'schedule_view_mode'];
    knownKeys.forEach(k => {
      const v = localStorage.getItem(k);
      if (v !== null) vals.push({ key: k, value: v });
    });
    setStorageValues(vals);
    setShowDebugInfo(true);
  };

  const loadCacheStats = () => {
    const keys = getAllKeys();
    const cacheKeys = keys.filter(k => k.startsWith('cache_'));
    let totalSize = 0;
    keys.forEach(k => { try { totalSize += (localStorage.getItem(k) || '').length; } catch {} });
    setCacheStats({ total: keys.length, cache: cacheKeys.length, sizeKB: Math.round(totalSize / 1024) });
  };

  const loadNotesStats = () => {
    const notes = loadAllNotes();
    const count = Object.keys(notes).length;
    const withHW = Object.values(notes).filter(n => n.homework).length;
    const withNotes = Object.values(notes).filter(n => n.noteText).length;
    setNotesStats({ total: count, withHomework: withHW, withNotes: withNotes });
  };

  const testApi = async (type) => {
    try {
      const start = Date.now();
      let result;
      if (type === 'groups') result = await apiService.getGroups(1);
      else if (type === 'news') result = await apiService.getNews(1);
      else result = await apiService.getCourses();
      const time = Date.now() - start;
      alert(`API тест (${type}): успешно за ${time}мс\nСтатус: ${result ? 'OK' : 'Пусто'}`);
    } catch (e) {
      alert(`API тест (${type}): ошибка\n${e.message}`);
    }
  };

  const ActionButton = ({ icon, label, onClick, color, destructive }) => (
    <div onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
      background: 'var(--surface-tertiary)', borderRadius: 12, border: '0.5px solid var(--border)',
      cursor: 'pointer', marginBottom: 8, transition: 'opacity 0.15s' }}
      onMouseDown={e => e.currentTarget.style.opacity = '0.7'}
      onMouseUp={e => e.currentTarget.style.opacity = '1'}
      onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
      <ion-icon name={icon} style={{ fontSize: 18, color: destructive ? '#ef4444' : (color || 'var(--accent)') }}></ion-icon>
      <span style={{ fontSize: 14, fontWeight: 500, color: destructive ? '#ef4444' : 'var(--text)' }}>{label}</span>
    </div>
  );

  return (
    <div style={{ padding: 16 }}>
      {/* API endpoint */}
      <SectionHeader title="API-эндпоинт" />
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8, paddingLeft: 4 }}>Текущий: {API_BASE_URL}</div>
      <SettingsGroup>
        <SettingsRow icon="server-outline" title="Кастомный эндпоинт" subtitle="Использовать свой сервер API" isFirst isLast
          onClick={() => { const v = !useCustomApi; setUseCustomApi(v); if (!v) localStorage.setItem('use_custom_api', 'false'); }}
          rightElement={<Toggle active={useCustomApi} onChange={(v) => { setUseCustomApi(v); if (!v) localStorage.setItem('use_custom_api', 'false'); }} />} />
      </SettingsGroup>
      {useCustomApi && (
        <div style={{ marginTop: 8 }}>
          <input type="url" value={customApiUrl} onChange={e => setCustomApiUrl(e.target.value)}
            placeholder="https://your-api.example.com/api"
            style={{ width: '100%', padding: '10px 14px', borderRadius: 12, border: '0.5px solid var(--border)',
              background: 'var(--surface-tertiary)', color: 'var(--text)', fontFamily: 'inherit', fontSize: 14, outline: 'none' }} />
          <button onClick={saveApiSettings} className="btn btn--primary btn--full" style={{ marginTop: 8, padding: '10px 20px' }}>
            <ion-icon name="checkmark" style={{ fontSize: 16 }}></ion-icon> Сохранить
          </button>
        </div>
      )}

      {/* Debug */}
      <SectionHeader title="Отладка" />
      <ActionButton icon="refresh-outline" label="Перезагрузить страницу" onClick={() => { if (window.confirm('Перезагрузить страницу?')) window.location.reload(); }} />
      <ActionButton icon="bug-outline" label="Показать отладочную информацию" onClick={loadDebugInfo} />
      {showDebugInfo && (
        <div style={{ background: 'var(--surface-tertiary)', borderRadius: 12, padding: 14, border: '0.5px solid var(--border)', marginBottom: 8, fontSize: 13 }}>
          <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Информация о сборке</div>
          <div style={{ color: 'var(--text-secondary)' }}>Версия: {APP_VERSION} ({BUILD_VER})</div>
          <div style={{ color: 'var(--text-secondary)' }}>Дата сборки: {BUILD_DATE}</div>
          <div style={{ fontWeight: 600, color: 'var(--text)', marginTop: 12, marginBottom: 6 }}>localStorage ({storageKeys.length} ключей)</div>
          {storageKeys.slice(0, 30).map((k, i) => <div key={i} style={{ color: 'var(--text-secondary)' }}>• {k}</div>)}
          {storageKeys.length > 30 && <div style={{ color: 'var(--text-secondary)' }}>...и ещё {storageKeys.length - 30}</div>}
          <div style={{ fontWeight: 600, color: 'var(--text)', marginTop: 12, marginBottom: 6 }}>Настройки</div>
          {storageValues.map((item, i) => (
            <div key={i} style={{ color: 'var(--text-secondary)', wordBreak: 'break-all' }}>
              • {item.key}: {item.value.length > 60 ? item.value.substring(0, 60) + '...' : item.value}
            </div>
          ))}
        </div>
      )}

      {/* Network & API */}
      <SectionHeader title="Сеть и API" />
      <ActionButton icon="globe-outline" label="Тест API: курсы" onClick={() => testApi('courses')} />
      <ActionButton icon="people-outline" label="Тест API: группы" onClick={() => testApi('groups')} />
      <ActionButton icon="newspaper-outline" label="Тест API: новости" onClick={() => testApi('news')} />

      {/* Cache */}
      <SectionHeader title="Кэш" />
      <ActionButton icon="analytics-outline" label="Статистика кэша" onClick={loadCacheStats} />
      {cacheStats && (
        <div style={{ background: 'var(--surface-tertiary)', borderRadius: 12, padding: 14, border: '0.5px solid var(--border)', marginBottom: 8, fontSize: 13 }}>
          <div style={{ color: 'var(--text-secondary)' }}>Всего записей: {cacheStats.total}</div>
          <div style={{ color: 'var(--text-secondary)' }}>Кэш-записей: {cacheStats.cache}</div>
          <div style={{ color: 'var(--text-secondary)' }}>Размер: ~{cacheStats.sizeKB} КБ</div>
        </div>
      )}
      <ActionButton icon="trash-outline" label="Очистить весь кэш" onClick={() => {
        if (window.confirm('Очистить весь кэш? Кэшированные данные расписания и новостей будут удалены.')) {
          const keys = getAllKeys().filter(k => k.startsWith('cache_'));
          keys.forEach(k => removeItem(k));
          setCacheStats(null);
          alert(`Удалено ${keys.length} кэш-записей`);
        }
      }} color="#f59e0b" />

      {/* Notes */}
      <SectionHeader title="Заметки и ДЗ" />
      <ActionButton icon="analytics-outline" label="Статистика заметок" onClick={loadNotesStats} />
      {notesStats && (
        <div style={{ background: 'var(--surface-tertiary)', borderRadius: 12, padding: 14, border: '0.5px solid var(--border)', marginBottom: 8, fontSize: 13 }}>
          <div style={{ color: 'var(--text-secondary)' }}>Всего: {notesStats.total}</div>
          <div style={{ color: 'var(--text-secondary)' }}>С заметками: {notesStats.withNotes}</div>
          <div style={{ color: 'var(--text-secondary)' }}>С ДЗ: {notesStats.withHomework}</div>
        </div>
      )}
      <ActionButton icon="trash-outline" label="Очистить все заметки" destructive onClick={() => {
        const count = Object.keys(loadAllNotes()).length;
        if (count === 0) { alert('Заметок нет'); return; }
        if (window.confirm(`Удалить все заметки (${count})? Это действие необратимо.`)) {
          clearAllNotes();
          setNotesStats(null);
          alert('Заметки удалены');
        }
      }} />

      {/* Danger zone */}
      <SectionHeader title="⚠️ Опасная зона" />
      <ActionButton icon="nuclear-outline" label="Тестовый краш (Error)" destructive onClick={() => {
        if (window.confirm('Это вызовет ошибку для тестирования. Продолжить?')) {
          throw new Error('[DevMenu] Test crash from Developer Menu');
        }
      }} />
      <ActionButton icon="trash-outline" label="Очистить всё localStorage" destructive onClick={() => {
        if (window.confirm('ВСЕ данные приложения будут сброшены. Это действие необратимо. Продолжить?')) {
          localStorage.clear();
          alert('localStorage очищен. Страница будет перезагружена.');
          window.location.reload();
        }
      }} />
      <ActionButton icon="close-circle-outline" label="Сбросить режим разработчика" destructive onClick={() => {
        if (window.confirm('Скрытые настройки будут недоступны. Активировать режим снова можно через пасхалку.')) {
          onResetDeveloperMode();
        }
      }} />
    </div>
  );
};

/* ======== MAIN SettingsScreen ======== */
const SettingsScreen = ({ theme, accentColor, setTheme, setAccentColor, isNewYearMode, onNewYearModeChange }) => {
  const location = useLocation();
  const routerNavigate = useNavigate();
  const currentScreen = location.pathname.split('/')[2] || null;
  const [screenFadeKey, setScreenFadeKey] = useState(0);
  const [versionTapCount, setVersionTapCount] = useState(0);
  const [easterEggActive, setEasterEggActive] = useState(false);
  const [secretMessage, setSecretMessage] = useState('');
  // developerMode: null = не инициализировано, true/false = известно
  const [developerMode, setDeveloperMode] = useState(() => {
    const saved = localStorage.getItem('developer_mode');
    return saved === null ? null : saved === 'true';
  });
  const [showConfetti, setShowConfetti] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const triggerConfetti = (msg) => {
    setShowConfetti(true);
    setToastMessage(msg);
    setTimeout(() => { setShowConfetti(false); setToastMessage(''); }, 3000);
  };

  // Синхронизация developerMode с localStorage и событием storage
  useEffect(() => {
    const updateDevMode = () => {
      const saved = localStorage.getItem('developer_mode');
      setDeveloperMode(saved === null ? null : saved === 'true');
    };
    window.addEventListener('storage', updateDevMode);
    return () => window.removeEventListener('storage', updateDevMode);
  }, []);

  const prevScreenRef = useRef(currentScreen);
  useEffect(() => {
    if (prevScreenRef.current !== currentScreen) {
      setScreenFadeKey(k => k + 1);
      prevScreenRef.current = currentScreen;
    }
  }, [currentScreen]);

  const navigateTo = (screen) => {
    routerNavigate(screen ? `/settings/${screen}` : '/settings');
  };

  // Guard: show 403 error for /settings/developer if not in developer mode
  useEffect(() => {
    // Не редиректим, пока developerMode не инициализирован
    if (developerMode === null) return;
    if (currentScreen === 'developer' && !developerMode) {
      routerNavigate('/error?code=403', { replace: true });
    }
  }, [currentScreen, developerMode, routerNavigate]);

  const easterEggMessages = [
    'Секретный уровень разблокирован! 🎉', 'ХГУ — лучший университет! 💻',
    'Разработано с любовью к студентам 💕', 'Кто ищет, тот всегда найдёт 👀',
    'А ты настойчивый! Продолжай в том же духе! 🔥', 'Ты обнаружил секретную функцию! 🥳',
    'ИТИ ХГУ гордится такими студентами! 🎓', 'Ты настоящий исследователь! 🔍',
    'Приложение стало лучше благодаря тебе! ✨', 'Ты заслужил виртуальное печенье! 🍪'
  ];

  const handleVersionPress = () => {
    const newCount = versionTapCount + 1;
    setVersionTapCount(newCount);

    if (newCount >= 5 && !easterEggActive) {
      setEasterEggActive(true);
      const msg = easterEggMessages[Math.floor(Math.random() * easterEggMessages.length)];
      setSecretMessage(msg);
      triggerConfetti('🎉 ' + msg);
      if (developerMode) {
        setVersionTapCount(0);
        setTimeout(() => { setEasterEggActive(false); setSecretMessage(''); }, 3000);
      }
    }
    if (newCount >= 7 && !developerMode) {
      localStorage.setItem('developer_mode', 'true');
      setDeveloperMode(true); // немедленно активируем режим
      triggerConfetti('🛠 Режим разработчика активирован!');
      setVersionTapCount(0); setEasterEggActive(false); setSecretMessage('');
      return;
    }
    if (newCount === 3 && !easterEggActive) {
      setToastMessage('Ого! Ты уже нажал 3 раза! Попробуй ещё 😉');
      setTimeout(() => setToastMessage(''), 2500);
    }
  };

  const resetDeveloperMode = () => {
    setDeveloperMode(false);
    localStorage.setItem('developer_mode', 'false');
    if (accentColor === 'orange' || accentColor === 'matrix') {
      setAccentColor('green');
      localStorage.setItem('accentColor', 'green');
    }
    if (theme === 'matrix') {
      setTheme('dark');
      localStorage.setItem('theme', 'dark');
    }
    navigateTo(null);
  };

  const clearAppCache = () => {
    if (window.confirm('После очистки кэша вы не сможете просматривать данные в оффлайн-режиме. Продолжить?')) {
      const keys = getAllKeys();
      keys.forEach(k => removeItem(k));
      alert('Кэш успешно очищен');
    }
  };

  const clearNotesHandler = () => {
    const count = Object.keys(loadAllNotes()).length;
    if (count === 0) { alert('Заметок и домашних заданий нет'); return; }
    if (window.confirm(`Будет удалено заметок и ДЗ: ${count}. Это действие необратимо. Продолжить?`)) {
      clearAllNotes();
      alert('Заметки удалены');
    }
  };

  /* --- render sub-screens --- */
  if (currentScreen === 'appearance') {
    return <div key={`sub-${screenFadeKey}`} className="sub-screen-enter"><AppearanceSettings theme={theme} accentColor={accentColor} setTheme={setTheme} setAccentColor={setAccentColor}
      developerMode={developerMode} isNewYearMode={isNewYearMode} onNewYearModeChange={onNewYearModeChange} /></div>;
  }
  if (currentScreen === 'about') {
    return <div key={`sub-${screenFadeKey}`} className="sub-screen-enter"><AboutScreen theme={theme} accentColor={accentColor} /></div>;
  }
  if (currentScreen === 'developer') {
    return <div key={`sub-${screenFadeKey}`} className="sub-screen-enter"><DeveloperMenu theme={theme} accentColor={accentColor} onResetDeveloperMode={resetDeveloperMode} /></div>;
  }

  /* --- Main settings screen --- */
  return (
    <div key={`main-${screenFadeKey}`} className={screenFadeKey > 0 ? 'screen settings-fade-enter' : 'screen'}>
      {/* ОФОРМЛЕНИЕ */}
      <SectionHeader title="Оформление" />
      <SettingsGroup>
        <SettingsRow icon="color-palette-outline" title="Внешний вид" subtitle="Тема, акцентный цвет, новогодний режим"
          onClick={() => navigateTo('appearance')} isFirst isLast />
      </SettingsGroup>

      {/* ДАННЫЕ И ХРАНИЛИЩЕ */}
      <SectionHeader title="Данные и хранилище" />
      <SettingsGroup>
        <SettingsRow icon="trash-outline" title="Очистить кэш" subtitle="Удалить сохранённые данные"
          onClick={clearAppCache} isFirst
          rightElement={<ion-icon name="trash-outline" style={{ fontSize: 18, color: 'var(--text-tertiary)' }}></ion-icon>} />
        <SettingsRow icon="document-text-outline" title="Очистить заметки и ДЗ" subtitle="Удалить все заметки к парам"
          onClick={clearNotesHandler} isLast
          rightElement={<ion-icon name="trash-outline" style={{ fontSize: 18, color: 'var(--text-tertiary)' }}></ion-icon>} />
      </SettingsGroup>

      {/* О ПРИЛОЖЕНИИ */}
      <SectionHeader title="О приложении" />
      <SettingsGroup>
        <SettingsRow icon="information-circle-outline" title="О приложении" subtitle="Информация и возможности"
          onClick={() => navigateTo('about')} isFirst />
        <SettingsRow icon="logo-github" title="GitHub репозиторий" subtitle="Исходный код проекта"
          onClick={() => window.open(GITHUB_REPO_URL, '_blank')} isLast
          rightElement={<ion-icon name="open-outline" style={{ fontSize: 18, color: 'var(--text-tertiary)' }}></ion-icon>} />
      </SettingsGroup>

      {/* РЕЖИМ РАЗРАБОТЧИКА */}
      {developerMode && (
        <>
          <SectionHeader title="🛠 Режим разработчика" />
          <SettingsGroup>
            <SettingsRow icon="construct-outline" title="Меню разработчика" subtitle="API, отладка, тестирование"
              onClick={() => navigateTo('developer')} isFirst isLast />
          </SettingsGroup>
        </>
      )}

      {/* ВЕРСИЯ */}
      <div onClick={handleVersionPress} style={{ background: 'var(--surface-secondary)', borderRadius: 14, border: easterEggActive ? '1.5px solid var(--accent)' : '0.5px solid var(--border)',
        boxShadow: easterEggActive ? '0 4px 12px var(--shadow-strong)' : '0 2px 6px var(--shadow-color)',
        padding: 16, marginTop: 24, textAlign: 'center', cursor: 'pointer', userSelect: 'none', transition: 'all 0.3s' }}>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Версия: {APP_VERSION}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>Сборка {BUILD_VER} от {BUILD_DATE}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>Разработано с ❤️ {APP_DEVELOPERS}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>При поддержке {APP_SUPPORTERS}</div>
        {easterEggActive && secretMessage && (
          <div style={{ marginTop: 12, padding: 10, background: 'var(--surface-tertiary)', borderRadius: 12, border: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <ion-icon name="sparkles" style={{ fontSize: 16, color: 'var(--accent)' }}></ion-icon>
            <span style={{ fontSize: 12, color: 'var(--accent)' }}>{secretMessage}</span>
          </div>
        )}
        {versionTapCount > 0 && versionTapCount < 5 && (
          <div style={{ fontSize: 10, color: 'var(--accent)', marginTop: 4, opacity: 0.7 }}>Нажатий: {versionTapCount}/5</div>
        )}
        {versionTapCount >= 5 && versionTapCount < 7 && !developerMode && (
          <div style={{ fontSize: 10, color: 'var(--accent)', marginTop: 4, opacity: 0.7 }}>Не останавливайся... ещё {7 - versionTapCount}</div>
        )}
      </div>

      <div style={{ height: 40 }} />

      {/* Confetti animation */}
      {showConfetti && (
        <div className="confetti-container">
          {Array.from({ length: 40 }).map((_, i) => (
            <div
              key={i}
              className="confetti-piece"
              style={{
                left: `${Math.random() * 100}%`,
                background: ['#ff6b6b', '#ffd93d', '#6bcb77', '#4d96ff', '#ff6bcb', '#a66cff'][i % 6],
                animationDelay: `${Math.random() * 1.5}s`,
                animationDuration: `${2 + Math.random() * 1.5}s`,
                width: `${6 + Math.random() * 8}px`,
                height: `${6 + Math.random() * 8}px`,
                borderRadius: Math.random() > 0.5 ? '50%' : '2px',
              }}
            />
          ))}
        </div>
      )}

      {/* Toast message */}
      {toastMessage && (
        <div style={{
          position: 'fixed',
          bottom: 100,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'var(--surface-secondary)',
          border: '1px solid var(--accent-glass-border)',
          borderRadius: 16,
          padding: '12px 20px',
          boxShadow: '0 8px 24px var(--shadow-strong)',
          zIndex: 10000,
          maxWidth: '80%',
          textAlign: 'center',
          animation: 'fadeInUp 0.3s ease-out',
        }}>
          <span style={{ fontSize: 14, color: 'var(--text)' }}>{toastMessage}</span>
        </div>
      )}
    </div>
  );
};

export default SettingsScreen;
